'use client';

/* eslint-disable @next/next/no-img-element */
import AccessiblePrice from '@/components/AccessiblePrice';
import { useJump } from '@/components/MagicJump';
import { StarFilled } from '@zeelool/icon';
import { useId, type MouseEvent } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { toProduct } from './productAdapter';
import type { BestsellerProduct } from './types';
import BestsellerFallbackImage from './BestsellerFallbackImage';
import { getBestsellerImageCandidates } from './bestsellerImages';
import {
  BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES,
  BESTSELLER_TAG_CLASS_NAMES,
} from './bestsellerStyles';
import { useValidExposure } from '@/hooks/useValidExposure';
import type { BestsellerProductTrackingHandlers } from './bestsellerTracking';
import BestsellerTrackedWish from './BestsellerTrackedWish';
import { isBestsellerProductUnavailable } from './bestsellerAvailability';
import BestsellerSoldOutOverlay from './BestsellerSoldOutOverlay';
import { useBestsellerDisplayPrice } from './bestsellerPrice';

function MobileRankBadge({ rank }: { rank: number }) {
  return (
    <div
      className="absolute left-[8px] top-0 z-[2] flex h-[30px] w-[26px] flex-col items-center bg-[#463ae8] font-bb text-[11px] leading-[11px] font-bold text-white"
      style={{ clipPath: 'polygon(0 0,100% 0,100% 100%,50% 80%,0 100%)' }}
    >
      <span className="mt-[2px]">TOP</span>
      <span>{rank}</span>
    </div>
  );
}

function MobilePurchasedPill({ product }: { product: BestsellerProduct }) {
  if (!product.purchased || !product.tagType) return null;
  const isTrend = product.tagType === 'trend';

  return (
    <span
      className={`inline-flex h-[21px] max-w-full items-center rounded-[3px] px-[4px] font-bb text-[12px] leading-[17px] ${
        isTrend ? BESTSELLER_TAG_CLASS_NAMES.trend : BESTSELLER_TAG_CLASS_NAMES.sales
      }`}
    >
      <span className="truncate">{product.purchased}</span>
    </span>
  );
}

function MobileProductInfo({
  product,
  top = false,
}: {
  product: BestsellerProduct;
  top?: boolean;
}) {
  const displayPrice = useBestsellerDisplayPrice(product, 'wap');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const hasPurchasedLabel = Boolean(product.purchased && product.tagType);
  const spacingClassName = top
    ? hasPurchasedLabel
      ? 'col-start-1 row-start-2 px-[12px] pb-[4px] pt-[12px]'
      : 'col-start-1 row-start-2 px-[12px] pb-[4px] pt-[16px]'
    : hasPurchasedLabel
      ? 'px-[8px] pb-[3px] pt-[6px]'
      : 'px-[8px] pb-[4px] pt-[8px]';

  return (
    <div className={`min-w-0 bg-white ${spacingClassName}`}>
      <div className="flex h-[14px] min-w-0 items-center justify-between gap-[4px]">
        <h3 className="truncate font-br text-[12px] leading-[14px] text-[#282828]">
          {product.name}
        </h3>
        <div
          className="flex h-[14px] shrink-0 items-center gap-[4px] leading-none text-[#282828]"
          data-mobile-ranking-rating
        >
          <StarFilled
            className="block h-[10px] w-[10px] shrink-0 leading-none text-[10px]"
            aria-hidden
          />
          <span className="translate-y-[1px] font-br text-[12px] leading-[14px]">
            {product.rating}
          </span>
        </div>
      </div>
      <div
        className={`flex min-w-0 flex-wrap items-baseline gap-x-[4px] overflow-hidden ${
          hasPurchasedLabel
            ? 'mt-[4px] max-h-[30px] gap-y-0 leading-[15px]'
            : 'mt-[8px] gap-y-[4px] leading-[17px]'
        }`}
      >
        <AccessiblePrice
          value={displayPrice.price}
          label={tPriceA11y('srCurrent')}
          className={`notranslate shrink-0 font-bb text-[14px] font-bold text-[#282828] ${
            hasPurchasedLabel ? 'leading-[15px]' : ''
          }`}
        />
        {displayPrice.originPrice ? (
          <AccessiblePrice
            as="del"
            value={displayPrice.originPrice}
            label={tPriceA11y('srOriginal')}
            className={`notranslate min-w-0 font-br text-[14px] text-[#666666] ${
              hasPurchasedLabel ? 'leading-[15px]' : ''
            }`}
          />
        ) : null}
      </div>
      <div className={hasPurchasedLabel ? 'mt-[2px]' : 'mt-[6px]'}>
        <MobilePurchasedPill product={product} />
      </div>
    </div>
  );
}

export default function MobileRankingCard({
  product,
  trackingHandlers,
  exposureKey = product.shopSku,
  returnTargetKey,
  onBeforeNavigate,
  onWishChange,
  top = false,
}: {
  product: BestsellerProduct;
  trackingHandlers: BestsellerProductTrackingHandlers;
  exposureKey?: string;
  returnTargetKey?: string;
  onBeforeNavigate?: () => void;
  onWishChange?: (isWish: number) => void;
  top?: boolean;
}) {
  const toJumpTarget = useJump();
  const wishProduct = toProduct(product);
  const unavailable = isBestsellerProductUnavailable(product);
  const soldOutDescriptionId = useId();
  const { ref: exposureRef } = useValidExposure({
    callback: trackingHandlers.onProductExposure,
    ts: exposureKey,
  });
  const jumpToProduct = () => {
    onBeforeNavigate?.();
    trackingHandlers.onProductClick();
    toJumpTarget({ jump_type: 4, Jump_target: product.href });
  };
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    jumpToProduct();
  };

  if (top) {
    return (
      <div
        ref={exposureRef}
        data-ranking-product-return-target={returnTargetKey}
        className={`${BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES.top} cursor-pointer`}
      >
        <a
          href={product.href}
          aria-label={product.name}
          aria-describedby={unavailable ? soldOutDescriptionId : undefined}
          className="absolute inset-0 z-[3] rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#463ae8]"
          onClick={handleLinkClick}
        />
        <div className="relative flex items-center justify-center">
          <MobileRankBadge rank={product.rank} />
          <BestsellerFallbackImage
            sources={getBestsellerImageCandidates(product, 'product')}
            alt={product.name}
            className={`absolute left-[18px] top-[30px] h-[70px] w-[140px] object-contain ${
              unavailable ? 'opacity-50' : ''
            }`}
          />
          <BestsellerSoldOutOverlay
            descriptionId={soldOutDescriptionId}
            product={product}
            platform="wap"
          />
        </div>
        <div className="absolute inset-y-0 right-0 w-1/2 overflow-hidden bg-[#eeeeee]">
          <BestsellerFallbackImage
            sources={[
              product.mobileModelImage,
              product.modelImage,
              ...getBestsellerImageCandidates(product, 'celebrity'),
              ...getBestsellerImageCandidates(product, 'material'),
              ...getBestsellerImageCandidates(product, 'product'),
            ]}
            alt=""
            className="absolute inset-0 block h-full w-full object-cover"
          />
        </div>
        <MobileProductInfo product={product} top />
        <BestsellerTrackedWish
          data={wishProduct}
          className="absolute right-[8px] top-[8px] z-[4] leading-none"
          iconClassName="text-[16px]"
          iconSize="1em"
          collectScene="bestsellers榜单"
          onCollect={trackingHandlers.onCollect}
          onWishChange={onWishChange}
        />
      </div>
    );
  }

  return (
    <div
      ref={exposureRef}
      data-ranking-product-return-target={returnTargetKey}
      className={`${BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES.compact} relative cursor-pointer`}
    >
      <a
        href={product.href}
        aria-label={product.name}
        aria-describedby={unavailable ? soldOutDescriptionId : undefined}
        className="absolute inset-0 z-[3] rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#463ae8]"
        onClick={handleLinkClick}
      />
      <div className="relative flex items-center justify-center">
        <MobileRankBadge rank={product.rank} />
        <BestsellerFallbackImage
          sources={getBestsellerImageCandidates(product, 'product')}
          alt={product.name}
          className={`absolute left-[16px] top-[38px] h-[70px] w-[140px] object-contain ${
            unavailable ? 'opacity-50' : ''
          }`}
        />
        <BestsellerSoldOutOverlay
          descriptionId={soldOutDescriptionId}
          product={product}
          platform="wap"
        />
      </div>
      <MobileProductInfo product={product} />
      <BestsellerTrackedWish
        data={wishProduct}
        className="absolute right-[8px] top-[8px] z-[4] leading-none"
        iconClassName="text-[16px]"
        iconSize="1em"
        collectScene="bestsellers榜单"
        onCollect={trackingHandlers.onCollect}
        onWishChange={onWishChange}
      />
    </div>
  );
}
